/* ============================================
   LeadChatPanel — La conversación del lead

   Los hilos salen de `useLeadConversaciones` (por
   CONTACTO, nunca por teléfono: en producción hay
   placeholders como +51999999999 compartidos por varios
   leads). Acá queda elegir cuál se está mirando.

   Embebe ChatWindow sin pasarle `conversation`,
   así omite su propio header (demasiado ancho para
   este panel) y queda el hilo + el input.
   ============================================ */
import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLeadConversaciones } from '@/hooks/useLeadConversaciones'
import ChatWindow from '@/components/chat/ChatWindow'
import { IconMessageSquare, IconExternalLink, IconChevronLeft } from '@/components/icons/Icons'
import type { Lead } from '@/lib/types'

interface LeadChatPanelProps {
  lead: Lead
  /** Teléfono del contacto principal, para el CTA cuando todavía no hay chat. */
  principalPhone?: string | null
  /**
   * Abre el hilo de UN contacto en particular (se entra desde su WhatsApp en
   * la barra lateral). Sin esto, el panel junta los hilos de todo el lead.
   */
  soloContactoId?: number | null
  /** Si viene, el panel muestra la vuelta a la lista de contactos. */
  onVolver?: () => void
}

const CANAL_LABEL: Record<string, string> = {
  whatsapp: 'WhatsApp',
  email: 'Email',
  web_chat: 'Chat web',
  sms: 'SMS',
  portal: 'Portal',
}

export default function LeadChatPanel({ lead, principalPhone, soloContactoId = null, onVolver }: LeadChatPanelProps) {
  const navigate = useNavigate()

  const { conversaciones, loading } = useLeadConversaciones(lead, soloContactoId)
  const [activaId, setActivaId] = useState<number | null>(null)

  /** Nombre por contacto_id, para etiquetar el selector cuando hay varios hilos. */
  const nombrePorContacto = useMemo(() => {
    const map: Record<number, string> = {}
    ;(lead.contactos || []).forEach(c => {
      map[c.contacto_id] = `${c.nombre} ${c.apellido || ''}`.trim()
    })
    return map
  }, [lead.contactos])

  // El hilo activo lo elige el panel, no el hook: el hook solo resuelve cuáles
  // son. Al cambiar la lista se vuelve al más reciente.
  useEffect(() => {
    setActivaId(conversaciones[0]?.id ?? null)
  }, [conversaciones])

  const activa = conversaciones.find(c => c.id === activaId) || null
  const nombreDelFoco = soloContactoId ? nombrePorContacto[soloContactoId] : null
  const telefonoDelFoco = soloContactoId
    ? (lead.contactos || []).find(c => c.contacto_id === soloContactoId)?.telefono || null
    : principalPhone

  return (
    <aside className="ld-chat">
      <div className="ld-chat__header">
        <div className="ld-chat__title">
          {onVolver ? (
            <button type="button" className="ld-chat__back" onClick={onVolver} title="Volver a contactos">
              <IconChevronLeft size={14} /> Contactos
            </button>
          ) : (
            <IconMessageSquare size={14} />
          )}
          {!onVolver && <span>Conversación</span>}
          {onVolver && nombreDelFoco && <span className="ld-chat__foco">{nombreDelFoco}</span>}
          {activa && <span className="ld-chat__canal">{CANAL_LABEL[activa.canal] || activa.canal}</span>}
        </div>
        {activa && (
          <button
            type="button"
            className="ld-chat__open"
            onClick={() => navigate(`/conversaciones/${activa.canal}/${activa.channel_id}/${activa.id}`)}
          >
            Abrir en bandeja <IconExternalLink size={12} />
          </button>
        )}
      </div>

      {conversaciones.length > 1 && (
        <div className="ld-chat__switcher">
          <select
            className="ld-chat__select"
            value={activaId ?? ''}
            onChange={e => setActivaId(Number(e.target.value))}
            aria-label="Elegir conversación"
          >
            {conversaciones.map(c => (
              <option key={c.id} value={c.id}>
                {(c.contacto_id && nombrePorContacto[c.contacto_id]) || c.chat_id} · {CANAL_LABEL[c.canal] || c.canal}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="ld-chat__body">
        {loading ? (
          <div className="ld-chat__empty">
            <div className="auth-spinner" style={{ width: 22, height: 22 }} />
            <p>Cargando conversación...</p>
          </div>
        ) : activa ? (
          <ChatWindow
            key={activa.id}
            conversacionId={activa.id}
            contactoId={activa.contacto_id}
            canal={activa.canal}
          />
        ) : (
          <div className="ld-chat__empty">
            <IconMessageSquare size={30} />
            <p>{nombreDelFoco ? `Todavía no hay chat con ${nombreDelFoco}.` : 'Este lead todavía no tiene conversación.'}</p>
            {telefonoDelFoco ? (
              <a
                href={`https://wa.me/${telefonoDelFoco.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-sm"
              >
                <IconMessageSquare size={13} /> Escribir por WhatsApp
              </a>
            ) : (
              <span className="text-muted-italic">Agregá un contacto con teléfono para iniciar el chat.</span>
            )}
          </div>
        )}
      </div>
    </aside>
  )
}
